import { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/base/Pagination";
import { movies } from "@/mocks/movies";
import { filterMovies, paginate } from "@/utils/filter";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 12;
const hotKeywords = ["科幻", "悬疑", "古装", "综艺", "高分", "犯罪"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [input, setInput] = useState(initialQuery);
  const [page, setPage] = useState(1);

  const keyword = searchParams.get("q") || "";

  usePageMeta({
    title: keyword ? `“${keyword}”的搜索结果 - 97视频` : "搜索影视 - 97视频",
    description: keyword
      ? `在97视频搜索“${keyword}”的相关影视内容，包含电影、电视剧、综艺等资源，快速找到你想看的影视作品。`
      : "在97视频搜索电影、电视剧、综艺等影视内容，支持关键词匹配，快速找到你想看的内容。",
    keywords: "97视频搜索,影视搜索,在线看电影,电视剧,综艺",
  });

  useEffect(() => {
    setInput(keyword);
    setPage(1);
  }, [keyword]);

  const results = useMemo(
    () => (keyword.trim() ? filterMovies(movies, { keyword }) : []),
    [keyword],
  );

  const { items, totalPages, total } = useMemo(
    () => paginate(results, page, PAGE_SIZE),
    [results, page],
  );

  const recommendations = useMemo(
    () => [...movies].sort((a, b) => b.plays - a.plays).slice(0, 6),
    [],
  );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = input.trim();
    if (value) {
      setSearchParams({ q: value });
    } else {
      setSearchParams({});
    }
  };

  const goToPage = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="mx-auto max-w-[1400px] px-4 py-8 md:px-8 md:py-10">
        <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
          搜索影视
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          输入片名、演员、导演或类型关键词，快速找到想看的内容
        </p>

        <form
          onSubmit={handleSubmit}
          className="mt-6 flex flex-col gap-3 sm:flex-row"
        >
          <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300/70 bg-background-100 px-4 py-3">
            <i className="ri-search-line text-lg text-foreground-500" aria-hidden="true"></i>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="例如：流浪地球、悬疑、张译"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
            />
          </div>
          <button
            type="submit"
            className="cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-8 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
          >
            搜索
          </button>
        </form>

        {!keyword && (
          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
            <span>热门搜索：</span>
            {hotKeywords.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => setSearchParams({ q: word })}
                className="cursor-pointer whitespace-nowrap rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-600 transition-colors hover:bg-accent-500 hover:text-background-50"
              >
                {word}
              </button>
            ))}
          </div>
        )}

        {keyword && (
          <div className="mt-6 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
            <span>
              关键词：
              <strong className="text-accent-500">“{keyword}”</strong>
            </span>
            <span className="text-foreground-300">|</span>
            <span>
              共 <strong className="text-accent-500">{total}</strong> 条结果
            </span>
          </div>
        )}

        {keyword && items.length > 0 && (
          <>
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5 xl:grid-cols-6">
              {items.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            <Pagination current={page} totalPages={totalPages} onChange={goToPage} />
          </>
        )}

        {keyword && items.length === 0 && (
          <div className="mt-6">
            <div className="flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-100 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-3xl" aria-hidden="true"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
                没有找到与“{keyword}”相关的影视
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                换个关键词试试，或看看下面的热门推荐
              </p>
            </div>

            <div className="mt-8">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
                猜你喜欢
              </h2>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5 xl:grid-cols-6">
                {recommendations.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </div>
        )}

        {!keyword && (
          <div className="mt-8">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
              热门影视推荐
            </h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5 xl:grid-cols-6">
              {recommendations.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            <div className="mt-8 text-center text-sm text-foreground-500">
              也可以
              <Link to="/category/all" className="cursor-pointer px-1 text-accent-500 hover:underline">
                浏览全部分类
              </Link>
              发现更多精彩内容
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}